import { defaultImgs } from "../../utils/defaultImages";
import { iconsImgs } from "../../../src/utils/icons";
import GetOfferSetting from "../Offers/GetOfferSetting";
import { getClubPhoto, getUserAvatar } from "../../utils/getMedia";
import { HostChatMessage, Invitation, Offer, Promoter } from "../../types";
import styles from "./OfferDetail.module.scss";
import { DateFormat, formatDate } from "../../utils/datePareser";
import AuthClient from "../../api/AuthClient";
import AcceptOfferApi from "../../api/AcceptOfferApi";
import { useNavigate } from "react-router";
import { useContext, useMemo, useState } from "react";
import Header from "../Header";
import UnacceptOfferApi from "../../api/UnAcceptAnOfferApi";
import AcceptOfferModal from "./AcceptOfferModal";
import { routes } from "../../routes/routes";
import { PartiesContext } from "../../GlobalContext/partiesContext";
import { PartyModel } from "../../types";
import { localStorageKeys } from "../../utils/localStorageKeys";
import AuthenticationResponse from "../../api/responses/AuthenticationResponse";
import GetHostChatMessages from "../../api/GetHostChatMessagesApi";
import { handleClubImgError } from "../../handleImgError";

export default function OfferDetail({
  promoter,
  offer,
  partyCity,
}: {
  promoter: Promoter;
  offer: Offer;
  partyCity: string;
}) {

  const [showAcceptOfferModal, setAcceptOfferModal] = useState(false);
  const handleShowAcceptOfferModal = () => setAcceptOfferModal(true);
  const handleAcceptOfferModalClose = () => setAcceptOfferModal(false);

  const { myPartiesInfo, selectedParty, setMyPartiesInfoContextData, setSelectedPartyContextData } = useContext(PartiesContext);
  const [showSkipOfferModal, setSkipOfferModal] = useState(false);

  const authClient = new AuthClient();
  const navigate = useNavigate();

  const storedLoginResponse =
    sessionStorage.getItem(localStorageKeys.loginResponse) ??
    localStorage.getItem(localStorageKeys.loginResponse) ??
    "{}";
  const userInfo = JSON.parse(storedLoginResponse) as AuthenticationResponse;

  const selectedPartyInfo = useMemo(() => {
    if (!selectedParty?.promotersOffers.some((promoterOffer) =>
      promoterOffer.offers.some((currentOffer) => currentOffer.id === offer.id)
    )) {
      return undefined;
    }

    return selectedParty;
  }, [offer.id, selectedParty]);

  const currentUserInvitation = useMemo(() => {
    return selectedPartyInfo?.invitations.find(
      (invitation) =>
        invitation.firstName === userInfo.firstName &&
        invitation.lastName === userInfo.lastName
    );
  }, [selectedPartyInfo, userInfo.firstName, userInfo.lastName]);

  const groupAdminInvitation = useMemo(() => {
    return selectedPartyInfo?.invitations.find((invitation) => invitation.isGroupAdmin);
  }, [selectedPartyInfo]);

  const isCurrentUserGroupAdmin = currentUserInvitation?.isGroupAdmin ?? false;

  const formattedDate = (inputDateString: Date) => {
    const date = new Date(inputDateString);
    return formatDate(date, false, DateFormat.Long);
  }

  const updateOffer = (parties: PartyModel[], offerId: string, isAcceptingOffer: boolean) => {

    return parties.map(party => {

      party.promotersOffers = party.promotersOffers.map(promoterOffer => {

        promoterOffer.offers = promoterOffer.offers.map(offer => {

          if (offer.id === offerId && isAcceptingOffer) {
            offer.isAccepted = true;
          }
          else {
            offer.isAccepted = false
          }
          return offer;
        });
        return promoterOffer;
      });
      return party;
    });
  };

  const AcceptTheOffer = async () => {
    if (!isCurrentUserGroupAdmin) {
      return false;
    }

    const api = new AcceptOfferApi(authClient);

    try {
      const response = await api.acceptOffer(offer.id);

      if (!response || response.statusCode >= 400) {
        throw new Error("Failed to accept the offer.");
      }

      const updatedPartiesInfo = updateOffer(myPartiesInfo, offer.id, true);
      setMyPartiesInfoContextData(updatedPartiesInfo);
      setSelectedPartyContextData((prev) => {
        if (!prev?.promotersOffers.some((promoterOffer) =>
          promoterOffer.offers.some((currentOffer) => currentOffer.id === offer.id)
        )) {
          return prev;
        }

        return updateOffer([prev], offer.id, true)[0];
      });

      handleAcceptOfferModalClose();
      return true;
    } catch (error) {
      console.error("Failed to accept offer:", error);
      return false;
    }
  }

  const SkipOffer = async () => {
    if (!isCurrentUserGroupAdmin) {
      return false;
    }

    const api = new UnacceptOfferApi(authClient);

    try {
      const response = await api.unAcceptOffer(offer.id);

      if (!response || response.statusCode >= 400) {
        throw new Error("Failed to skip the offer.");
      }

      const updatedPartiesInfo = updateOffer(myPartiesInfo, offer.id, false);
      setMyPartiesInfoContextData(updatedPartiesInfo);
      setSelectedPartyContextData((prev) => {
        if (!prev?.promotersOffers.some((promoterOffer) =>
          promoterOffer.offers.some((currentOffer) => currentOffer.id === offer.id)
        )) {
          return prev;
        }

        return updateOffer([prev], offer.id, false)[0];
      });

      navigate(routes.Root);
      return true;
    } catch (error) {
      console.error("Failed to skip offer:", error);
      return false;
    }
  }

  const navigateToChat = () => {
    const myInfo = selectedPartyInfo?.invitations.find(
      (el: Invitation) =>
        el.firstName === userInfo.firstName &&
        el.lastName === userInfo.lastName
    );

    const hostChatMessages = new GetHostChatMessages(authClient);
    hostChatMessages.getChatMessages(offer.id).then((response) => {
      const messages: HostChatMessage[] = response.data ?? [];

      navigate(routes.ChatPages, {
        replace: true,
        state: {
          messages,
          offer,
          groupAdminName: `${groupAdminInvitation?.firstName ?? ""} ${groupAdminInvitation?.lastName ?? ""}`.trim(),
          groupAdminId: groupAdminInvitation?.id,
          promoterName: promoter.FirstName,
          promoterId: promoter.id,
          currentUserId: myInfo?.id,
          bookingType: selectedPartyInfo?.party.bookingType
        }
      });
    });
  }

  return (
    <>
      <Header />
      <AcceptOfferModal showAcceptOfferModal={showAcceptOfferModal} handleAcceptOfferModalClose={handleAcceptOfferModalClose} acceptOffer={AcceptTheOffer}
        onAcceptDone={navigateToChat} showSkipOfferModal={showSkipOfferModal} setSkipOfferModal={setSkipOfferModal} skipOffer={SkipOffer} />
      <div className="centerGroup">
        <div className="desktopUI">
          <div className="d-flex justify-content-start space col mb-1">
            <button className='BtnTop yellowTxt btn backBtn' onClick={() => navigate(-1)}>Back</button>
          </div>
        </div>
      </div>

      <div className={styles.offerDetailPage}>
        <div className={styles.offerDetailContainer}>
          <div className={styles.senderImageContainer}>
            <img
              className={styles.senderImage}
              src={getUserAvatar(promoter.id) ?? defaultImgs.Avatar}
              alt=""
              onError={(event) => { (event.target as HTMLImageElement).src = defaultImgs.Avatar; }}
            />
          </div>
          <h3 className={styles.senderName}>{promoter.FirstName}</h3>
          {/* <div className={styles.ratingBox}> --Commented the rating to get displayed
            <img src={iconsImgs.WhiteStar} alt="Rating" />
            <span>{`Rating: ${Number(promoter.Rating).toFixed(1)}`}</span>
          </div> */}
          <div className={styles.infoContainer}>
            <div className={styles.infoBox}>
              <img
                src={iconsImgs.UpcomingEvents}
                alt="upcomingEvents"
                className={styles.icon}
              />
              <span className={styles.info}>{formattedDate(offer.partyDate)}</span>
            </div>

            <div className={styles.infoBox}>
              <img src={iconsImgs.Location} alt="location" className={styles.icon} />
              <div className={styles.clubImgBox}>
                <img
                  src={
                    getClubPhoto({
                      clubName: offer.clubName,
                      cityName: partyCity,
                    }) ?? defaultImgs.ClubImg
                  }
                  alt=""
                  onError={handleClubImgError}
                />

                <span>{offer.clubName}</span>
              </div>
            </div>

            {offer.offerType && offer.offerType === "TableBooking" &&
              <div className={styles.infoBox}>
                <img
                  src={iconsImgs.TableBooking}
                  alt="tableBooking"
                  className={styles.icon}
                />

                <span className={styles.info}>Table</span>
              </div>
            }

            {offer.offerType && offer.offerType === "GuestList" &&
              <div className={styles.infoBox}>
                <img
                  src={iconsImgs.GuestListBooking}
                  alt="guestList"
                  className={styles.icon}
                />

                <span className={styles.info}>GuestList</span>
              </div>
            }

            {Number(offer.minSpend) > 0 &&
              <div className={styles.infoBox}>
                <img
                  src={iconsImgs.MinimumSpend}
                  alt="minSpend"
                  className={styles.icon}
                />
                <span className={styles.info}>{offer.minSpend}</span>
              </div>
            }

            {offer.options.map((el, index) => (
              <div key={index} className={styles.infoBox}>
                <div className={styles.icon}>
                  <GetOfferSetting option={el} isIcon />
                </div>

                <span className={styles.info}>
                  <GetOfferSetting option={el} isIcon={false} />
                </span>
              </div>
            ))}
          </div>

          {isCurrentUserGroupAdmin && (
            !offer.isAccepted ? <button
              className={styles.acceptBtn}
              onClick={handleShowAcceptOfferModal}
            >
              Accept this offer
            </button> :
              <button
                className={styles.acceptBtn}
                onClick={() => setSkipOfferModal(true)}
              >
                Skip this offer
              </button>
          )}
        </div>
      </div>
    </>
  );
}
